import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppSelector } from "@/data/hooks";
import { getAssessmentId } from "@/data/task";
import { DuplicatePastAssessmentCallback } from "@/data/templates";
import { selectSortedTaskBlocks } from "@/features/multi-format/task-layout";
import { useCopyAssessment } from "@/ui/templates/templatesHooks";
import { logAppError } from "@/utils/logger";

import { useIsEditorEmpty } from "../editorHooks";
import { useShowEditorContentReplacementModal } from "../modals";

export function useOnDuplicatePastAssessment(): DuplicatePastAssessmentCallback {
  const assessmentIdBeingWorkedOn = useAppSelector(getAssessmentId);
  const copyAssessment = useCopyAssessment();
  const isEditorEmpty = useIsEditorEmpty();
  const taskBlocks = useAppSelector(selectSortedTaskBlocks);
  const emptyTaskBlocks = taskBlocks.length === 0;
  const showContentReplaceModal = useShowEditorContentReplacementModal();
  const { track } = useJitsu();
  return useCallback(
    async (
      pastAssessmentId: string,
      pastAssessmentName: string = "Past assessment",
      instructionSheetVersionId: string
    ) => {
      if (!(pastAssessmentId && assessmentIdBeingWorkedOn)) {
        const message = `One of pastAssessmentId (${pastAssessmentId}) or
          assessmentIdBeingWorkedOn (${assessmentIdBeingWorkedOn})
          not found when trying to copy past assessment`;
        logAppError(message);
        return;
      }

      if (isEditorEmpty && emptyTaskBlocks) {
        await copyAssessment(
          assessmentIdBeingWorkedOn,
          pastAssessmentName,
          instructionSheetVersionId
        );
        track(JitsuEvent.TEMPLATE_SHEET_USED, {
          assessment_id: pastAssessmentId,
          assessment_name: pastAssessmentName,
          sheet_id: instructionSheetVersionId,
        });
      } else {
        showContentReplaceModal({
          useCase: {
            type: "past-assessment-replacement",
            instructionSheetVersionId,
            currentAssessmentId: assessmentIdBeingWorkedOn,
            pastAssessmentId,
            pastAssessmentName,
          },
        });
      }
    },
    [
      assessmentIdBeingWorkedOn,
      copyAssessment,
      isEditorEmpty,
      showContentReplaceModal,
      track,
      emptyTaskBlocks,
    ]
  );
}
